import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { useMutation, useQuery } from "@apollo/client/react";
import CustomSnackbar from "./Snackbar";
import AppDatePicker from "./AppDatePicker";
import AppTimePicker from "./AppTimePicker";
import themestyle from "../theme";
import { FormLabel, textFieldSX } from "../utils/formField";
import { CREATE_EVENT, UPDATE_EVENT } from "../graphql/mutation";
import { GET_EVENT_BY_ID, GET_INSTRUCTOR_LIST, GET_EVENT_LIST, GET_DASHBOARD_COUNTS } from "../graphql/query";
import {
  CreateEventMutationResponse,
  EventFormMode,
  EventInput,
  GetEventByIdResponse,
  UpdateEventMutationResponse,
} from "../interfaces/event.interface";
import { resizeFile } from "../utils/imageResizer";

const normalizeDateInput = (value?: string | null) => {
  if (!value) return "";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
};

const parseDateParts = (value?: string | null) => {
  const normalized = normalizeDateInput(value);
  if (!normalized) return null;
  const [year, month, day] = normalized.split("-").map(Number);
  if (!year || !month || !day) return null;
  return { year, month, day };
};

const getDaySpanInclusive = (start?: string | null, end?: string | null) => {
  const startParts = parseDateParts(start);
  const endParts = parseDateParts(end);
  if (!startParts || !endParts) return null;

  const startUtc = Date.UTC(startParts.year, startParts.month - 1, startParts.day);
  const endUtc = Date.UTC(endParts.year, endParts.month - 1, endParts.day);
  if (endUtc < startUtc) return null;

  return Math.floor((endUtc - startUtc) / (1000 * 3600 * 24)) + 1;
};

const validateOfferDiscountPercent = (value?: string) => {
  const percent = Number(value);
  if (Number.isNaN(percent)) return "Discount is required";
  if (percent < 0 || percent > 100) {
    return "Discount must be between 0 and 100";
  }
  return "";
};

const validateOfferDiscountAmount = (
  value: string | undefined,
  standardPrice: number,
) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return "Discount amount is required";
  if (amount < 0 || amount > standardPrice) {
    return `Discount amount must be between 0 and ${standardPrice}`;
  }
  return "";
};

const sanitizeNonNegativeNumber = (value: number) => {
  if (Number.isNaN(value)) return 0;
  return Math.max(0, value);
};

const containsNumericValue = (value?: string) => /\d/.test(value ?? "");

type AddEventFormProps = {
  mode?: EventFormMode;
  id?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  onEdit?: () => void;
};

const AddEventForm: React.FC<AddEventFormProps> = ({
  mode = "add",
  id,
  onSuccess,
  onCancel,
  onEdit,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const galleryInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      return "Only JPG, JPEG, and PNG files are allowed";
    }
    if (file.size > maxSize) {
      return "File size must be less than 5MB";
    }
    return null;
  };

  const [isDragActiveGallery, setIsDragActiveGallery] = useState(false);
  const [isDragActiveThumbnail, setIsDragActiveThumbnail] = useState(false);

  const [formData, setFormData] = useState<EventInput>({
    eventName: "",
    location: "",
    startDate: "",
    endDate: "",
    duration: "",
    startTime: "",
    endTime: "",
    standardPrice: 0,
    gallery: [],
    thumbnail: null,
    instructors: [],
    description: "",
    offers: [],
  });

  const [existingGallery, setExistingGallery] = useState<string[]>([]);
  const [existingThumbnail, setExistingThumbnail] = useState<string | null>(
    null,
  );
  const [selectedThumbIndex, setSelectedThumbIndex] = useState<number>(-1);
  const [standardPriceInput, setStandardPriceInput] = useState<string>("0");
  const standardPriceRef = useRef<string>("0");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">(
    "success",
  );

  const [currentGlobalDate, setCurrentGlobalDate] = useState<string>("");

  useEffect(() => {
    const fetchGlobalTime = async () => {
      try {
        const response = await fetch(
          "https://worldtimeapi.org/api/timezone/Etc/UTC",
        );
        const data = await response.json();
        if (data && data.datetime) {
          const globalDate = data.datetime.split("T")[0];
          setCurrentGlobalDate(globalDate);
        }
      } catch (error) {
        console.error(error);
        const today = new Date().toISOString().split("T")[0];
        setCurrentGlobalDate(today);
      }
    };
    fetchGlobalTime();
  }, []);

  const isViewMode = mode === "view";

  const { data: eventData } = useQuery<GetEventByIdResponse, { id: string }>(
    GET_EVENT_BY_ID,
    {
      variables: { id: id || "" },
      skip: mode === "add" || !id,
      fetchPolicy: "network-only",
    },
  );

  const { data: instructorData } = useQuery(GET_INSTRUCTOR_LIST, {
    variables: { page: 1, perPage: 100, search: "" },
    fetchPolicy: "network-only",
  });

  const instructorOptions = useMemo(
    () =>
      (instructorData?.getInstructorList?.data || []).map((item: any) => ({
        label: item.fullname,
        value: item.id,
      })),
    [instructorData],
  );

  const [createEvent, { loading: createLoading }] = useMutation<
    CreateEventMutationResponse,
    { input: EventInput }
  >(CREATE_EVENT, {
    refetchQueries: [
      { query: GET_EVENT_LIST, variables: { page: 1, perPage: 2 } },
      { query: GET_EVENT_LIST, variables: { page: 1, perPage: 10 } },
      { query: GET_DASHBOARD_COUNTS },
    ],
    awaitRefetchQueries: true,
  });
  const [updateEvent, { loading: updateLoading }] = useMutation<
    UpdateEventMutationResponse,
    { id: string; input: Partial<EventInput> }
  >(UPDATE_EVENT, {
    refetchQueries: [
      { query: GET_EVENT_LIST, variables: { page: 1, perPage: 2 } },
      { query: GET_EVENT_LIST, variables: { page: 1, perPage: 10 } },
      { query: GET_DASHBOARD_COUNTS },
    ],
    awaitRefetchQueries: true,
  });

  const loading = createLoading || updateLoading;

  useEffect(() => {
    if (eventData?.getEventById?.success && eventData.getEventById.data) {
      const existing = eventData.getEventById.data;
      setFormData({
        eventName: existing.eventName || "",
        location: existing.location || "",
        startDate: normalizeDateInput(existing.startDate),
        endDate: normalizeDateInput(existing.endDate),
        duration: existing.duration || "",
        startTime: existing.time?.split("-")[0] || "",
        endTime: existing.time?.split("-")[1] || "",
        standardPrice: Number(existing.standardPrice) || 0,
        gallery: [],
        thumbnail: null,
        instructors: existing.instructors || [],
        description: existing.description || "",
        offers:
          existing.offers?.map((o: any) => {
            const offerPrice = Number(o.offerPrice) || 0;
            const standardPrice = Number(existing.standardPrice) || 0;
            const discountAmt = standardPrice > 0 ? (standardPrice - offerPrice) : 0;
            const discountPct = standardPrice > 0 ? (discountAmt / standardPrice) * 100 : 0;
            return {
              promoCode: o.promoCode || "",
              offerStartDate: normalizeDateInput(o.offerStartDate),
              offerEndDate: normalizeDateInput(o.offerEndDate),
              offerPrice: offerPrice,
              discountAmount: parseFloat(discountAmt.toFixed(2)).toString(),
              discountPercent: parseFloat(discountPct.toFixed(2)).toString(),
            };
          }) || [],
      });
      setExistingGallery(existing.gallery || []);
      setExistingThumbnail(existing.thumbnail || null);
      setSelectedThumbIndex(-1);
      const existingPrice = String(Number(existing.standardPrice) || 0);
      standardPriceRef.current = existingPrice;
      setStandardPriceInput(existingPrice);
    }
  }, [eventData]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!formData.eventName.trim())
      nextErrors.eventName = "Event name is required";
    if (!formData.location.trim()) nextErrors.location = "Location is required";
    else if (containsNumericValue(formData.location))
      nextErrors.location = "Location cannot contain numbers";
    if (!formData.startDate) nextErrors.startDate = "Start date is required";
    if (!formData.endDate) nextErrors.endDate = "End date is required";
    if (
      formData.startDate &&
      formData.endDate &&
      formData.endDate < formData.startDate
    ) {
      nextErrors.endDate = "End date cannot be before start date";
    }
    if (!formData.duration.trim()) nextErrors.duration = "Duration is required";
    if (!formData.startTime) nextErrors.startTime = "Start time is required";
    if (!formData.endTime) nextErrors.endTime = "End time is required";
    if (
      formData.startTime &&
      formData.endTime &&
      toMinutes(formData.startTime) >= toMinutes(formData.endTime)
    ) {
      nextErrors.endTime = "End time must be after start time";
    }
    if (
      formData.standardPrice === undefined ||
      formData.standardPrice === null ||
      Number.isNaN(Number(formData.standardPrice)) ||
      Number(formData.standardPrice) < 0
    ) {
      nextErrors.standardPrice = "Base ticket price cannot be negative";
    }

    // In edit mode, existing gallery items count — only require new upload if there are none existing
    if (
      (!formData.gallery || formData.gallery.length === 0) &&
      existingGallery.length === 0
    ) {
      nextErrors.gallery = "At least one gallery image is required";
    }

    if (
      !formData.thumbnail &&
      !existingThumbnail &&
      selectedThumbIndex === -1
    ) {
      nextErrors.thumbnail = "Thumbnail image is required";
    }

    if (!formData.description || !formData.description.trim()) {
      nextErrors.description = "Description is required";
    }

    if (formData.instructors.length === 0)
      nextErrors.instructors = "Select at least one instructor";

    formData.offers.forEach((offer, idx) => {
      if (!offer.promoCode || !offer.promoCode.trim()) {
        nextErrors[`offerPromoCode_${idx}`] = "Offer name is required";
      }
      const discountPercentError = validateOfferDiscountPercent(
        offer.discountPercent,
      );
      if (discountPercentError) {
        nextErrors[`offerDiscountPercent_${idx}`] = discountPercentError;
      }
      if (!offer.offerStartDate) {
        nextErrors[`offerStartDate_${idx}`] = "Start date required";
      }
      if (!offer.offerEndDate) {
        nextErrors[`offerEndDate_${idx}`] = "End date required";
      }
      if (
        offer.offerStartDate &&
        offer.offerEndDate &&
        offer.offerEndDate < offer.offerStartDate
      ) {
        nextErrors[`offerEndDate_${idx}`] =
          "End date cannot be before start date";
      }
      if (offer.offerStartDate && formData.startDate && offer.offerStartDate < formData.startDate) {
        nextErrors[`offerStartDate_${idx}`] = `Offer start date cannot be before event start date (${formData.startDate})`;
      }
      if (offer.offerStartDate && formData.endDate && offer.offerStartDate > formData.endDate) {
        nextErrors[`offerStartDate_${idx}`] = `Offer start date cannot be after event end date (${formData.endDate})`;
      }
      if (offer.offerEndDate && formData.startDate && offer.offerEndDate < formData.startDate) {
        nextErrors[`offerEndDate_${idx}`] = `Offer end date cannot be before event start date (${formData.startDate})`;
      }
      if (offer.offerEndDate && formData.endDate && offer.offerEndDate > formData.endDate) {
        nextErrors[`offerEndDate_${idx}`] = `Offer end date cannot be after event end date (${formData.endDate})`;
      }
      if (
        offer.offerPrice === undefined ||
        offer.offerPrice === null ||
        Number(offer.offerPrice) > Number(formData.standardPrice) ||
        Number(offer.offerPrice) < 0 ||
        isNaN(Number(offer.offerPrice))
      ) {
        nextErrors[`offerPrice_${idx}`] = "Valid discount amount required";
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const toMinutes = (timeStr: string) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  const handleGalleryDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (isViewMode) return;
    event.preventDefault();
    setIsDragActiveGallery(true);
  };

  const handleGalleryDragLeave = () => {
    if (isViewMode) return;
    setIsDragActiveGallery(false);
  };

  const handleGalleryDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (isViewMode) return;
    event.preventDefault();
    setIsDragActiveGallery(false);
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length) {
      const currentGallery = Array.isArray(formData.gallery)
        ? formData.gallery
        : [];
      
      const validFiles: File[] = [];
      let lastError = "";

      for (const file of files) {
        const error = validateFile(file);
        if (error) {
          lastError = error;
        } else {
          try {
            const resizedFile = await resizeFile(file);
            validFiles.push(resizedFile);
          } catch (err) {
            console.error(err);
            validFiles.push(file);
          }
        }
      }

      if (lastError) {
        setErrors((prev) => ({ ...prev, gallery: lastError }));
        return;
      }

      if (existingGallery.length + currentGallery.length + validFiles.length > 3) {
        setSnackbarMessage(
          "You can only upload up to 3 images in the Event Gallery.",
        );
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }
      handleInputChange("gallery", [...currentGallery, ...validFiles]);
      setSelectedThumbIndex(-1);
    }
  };

  const handleThumbnailDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (isViewMode) return;
    event.preventDefault();
    setIsDragActiveThumbnail(true);
  };

  const handleThumbnailDragLeave = () => {
    if (isViewMode) return;
    setIsDragActiveThumbnail(false);
  };

  const handleThumbnailDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (isViewMode) return;
    event.preventDefault();
    setIsDragActiveThumbnail(false);
    const file = event.dataTransfer.files?.[0];
      try {
        const resizedFile = await resizeFile(file);
        handleInputChange("thumbnail", resizedFile);
      } catch (err) {
        console.error(err);
        handleInputChange("thumbnail", file);
      }
      setSelectedThumbIndex(-1);
  };

  const handleInputChange = (name: keyof EventInput, value: any) => {
    const nextValue = value;

    setFormData((prev) => {
      const updated = { ...prev, [name]: nextValue };

      if (name === "startDate" || name === "endDate") {
        const start = name === "startDate" ? nextValue : prev.startDate;
        const end = name === "endDate" ? nextValue : prev.endDate;

        if (start && end && end >= start) {
          const daysDiff = getDaySpanInclusive(start, end);
          updated.duration = daysDiff
            ? `${daysDiff} Day${daysDiff > 1 ? "s" : ""}`
            : "";
        } else {
          updated.duration = "";
        }
      }

      const fieldName = name as string;
      if (fieldName === "standardPrice") {
        const rawStandardPrice =
          typeof nextValue === "string" ? nextValue : String(nextValue);
        standardPriceRef.current = rawStandardPrice;
        setStandardPriceInput(rawStandardPrice);
        const newStandardPrice = sanitizeNonNegativeNumber(Number(rawStandardPrice));
        updated.standardPrice = newStandardPrice;
        if (!isNaN(newStandardPrice)) {
          updated.offers = updated.offers.map((offer) => {
            if (
              offer.offerPrice !== undefined &&
              offer.offerPrice !== null &&
              offer.offerPrice >= 0
            ) {
              const oldStandard = Number(prev.standardPrice) || 0;
              if (newStandardPrice === 0) {
                return {
                  ...offer,
                  offerPrice: 0,
                  discountAmount: "0",
                  discountPercent: "0",
                };
              }
              if (oldStandard > 0) {
                const discountRatio =
                  (oldStandard - Number(offer.offerPrice)) / oldStandard;
                const newPrice =
                  newStandardPrice - newStandardPrice * discountRatio;
                const finalPrice = Math.max(0, parseFloat(newPrice.toFixed(2)));
                return {
                  ...offer,
                  offerPrice: finalPrice,
                  discountAmount: parseFloat((newStandardPrice - finalPrice).toFixed(2)).toString(),
                  discountPercent: parseFloat((discountRatio * 100).toFixed(2)).toString(),
                };
              } else {
                 return {
                  ...offer,
                  offerPrice: newStandardPrice,
                  discountAmount: "0",
                  discountPercent: "0",
                };
              }
            }
            return offer;
          });
        }
      }

      return updated;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const currentThumbnailPreview = useMemo(() => {
    if (formData.thumbnail instanceof File) {
      return URL.createObjectURL(formData.thumbnail);
    }
    if (
      selectedThumbIndex >= 0 &&
      formData.gallery &&
      formData.gallery[selectedThumbIndex]
    ) {
      return URL.createObjectURL(formData.gallery[selectedThumbIndex]);
    }
    return existingThumbnail;
  }, [
    existingThumbnail,
    formData.gallery,
    formData.thumbnail,
    selectedThumbIndex,
  ]);

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      if (mode === "edit" && id) {
        const { startTime, endTime, ...restFormData } = formData;
        const parsedStandardPrice = sanitizeNonNegativeNumber(Number(standardPriceRef.current));
        const input: Partial<EventInput> = {
          ...restFormData,
          standardPrice: parsedStandardPrice,
          description: formData.description || "",
          offers: formData.offers.map(({ discountAmount, discountPercent, ...o }) => ({
            ...o,
            offerPrice: Number(o.offerPrice) || 0,
          })),
        };

        // Gallery: send new files if uploaded, otherwise signal backend to keep existing
        if (formData.gallery && formData.gallery.length > 0) {
          input.gallery = formData.gallery;
        }
        // Always send retainedGallery so backend knows which existing images to keep.
        // The user may have removed some via the ✕ button, so existingGallery reflects that.
        (input as any).retainedGallery = existingGallery;

        // Thumbnail: send new file if picked; if user selected a new gallery image as thumb,
        // send that file; otherwise omit so backend preserves the existing thumbnail
        if (formData.thumbnail) {
          input.thumbnail = formData.thumbnail;
        } else if (
          selectedThumbIndex >= 0 &&
          formData.gallery &&
          formData.gallery[selectedThumbIndex]
        ) {
          input.thumbnail = formData.gallery[selectedThumbIndex];
        }
        // If neither new gallery nor new thumbnail was provided, explicitly omit both
        // so the backend resolver keeps what is already stored (its merge uses ?? existing).

        const finalInput = {
          ...input,
          time: `${formData.startTime}-${formData.endTime}`,
        };

        const { data } = await updateEvent({
          variables: { id, input: finalInput as any },
        });
        if (!data?.updateEvent?.success) {
          setSnackbarMessage(
            data?.updateEvent?.message || "Failed to update event",
          );
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(
          data.updateEvent.message || "Event updated successfully",
        );
        setSnackbarType("success");
      } else {
        const { startTime, endTime, ...restFormData } = formData;
        const parsedStandardPrice = sanitizeNonNegativeNumber(Number(standardPriceRef.current));
        const input: Record<string, any> = {
          ...restFormData,
          standardPrice: parsedStandardPrice,
          offers: formData.offers.map(({ discountAmount, discountPercent, ...o }) => ({
            ...o,
            offerPrice: Number(o.offerPrice) || 0,
          })),
        };

        if (
          !formData.thumbnail &&
          selectedThumbIndex >= 0 &&
          formData.gallery &&
          formData.gallery[selectedThumbIndex]
        ) {
          input.thumbnail = formData.gallery[selectedThumbIndex];
        }

        const finalInput = {
          ...input,
          time: `${formData.startTime}-${formData.endTime}`,
        };

        const { data } = await createEvent({
          variables: { input: finalInput as any },
        });
        if (!data?.createEvent?.success) {
          setSnackbarMessage(
            data?.createEvent?.message || "Failed to create event",
          );
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(
          data.createEvent.message || "Event created successfully",
        );
        setSnackbarType("success");
      }

      setSnackbarOpen(true);
      if (onSuccess) setTimeout(onSuccess, 900);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "20px",
          border: "1px solid #E7EEDC",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: themestyle.colors.lightGreenBg,
            px: 3,
            py: 2.5,
          }}
        >
          <Typography
            fontSize="20px"
            fontWeight={700}
            color={themestyle.colors.activeGreen}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            Event Information
          </Typography>
        </Box>

        <Box
          p={3}
          display="flex"
          flexDirection="column"
          gap={3}
          sx={{ px: { xs: 2, sm: 3, md: 5 } }}
        >
          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>Name of the Event *</FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                value={formData.eventName}
                placeholder="eg., Boogie Down 2026"
                onChange={(e) => handleInputChange("eventName", e.target.value)}
                error={!!errors.eventName}
                helperText={errors.eventName}
                disabled={isViewMode}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>Location *</FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                value={formData.location}
                placeholder="Bangalore"
                onChange={(e) => handleInputChange("location", e.target.value)}
                error={!!errors.location}
                helperText={errors.location}
                disabled={isViewMode}
              />
            </Box>
          </Box>

          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>Start Date *</FormLabel>
              <AppDatePicker
                fullWidth
                value={formData.startDate}
                onChange={(v) => handleInputChange("startDate", v)}
                error={!!errors.startDate}
                helperText={errors.startDate}
                disabled={isViewMode}
                minDate={currentGlobalDate}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>End Date *</FormLabel>
              <AppDatePicker
                fullWidth
                value={formData.endDate}
                onChange={(v) => handleInputChange("endDate", v)}
                error={!!errors.endDate}
                helperText={errors.endDate}
                disabled={isViewMode}
                minDate={formData.startDate || currentGlobalDate}
              />
            </Box>
          </Box>

          <Box
            display="flex"
            gap={3}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1}>
              <FormLabel>Duration *</FormLabel>
              <TextField
                fullWidth
                sx={{
                  ...textFieldSX,
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#000",
                    backgroundColor: "#f5f5f5",
                  },
                }}
                value={formData.duration}
                placeholder="eg., 1 Day"
                disabled
              />
            </Box>
            <Box flex={1.5}>
              <Box display="flex" gap={2}>
                <Box flex={1}>
                  <FormLabel>Start Time *</FormLabel>
                  <AppTimePicker
                    fullWidth
                    value={formData.startTime}
                    onChange={(v) =>
                      handleInputChange("startTime", v)
                    }
                    error={!!errors.startTime}
                    helperText={errors.startTime}
                    disabled={isViewMode}
                  />
                </Box>
                <Box flex={1}>
                  <FormLabel>End Time *</FormLabel>
                  <AppTimePicker
                    fullWidth
                    value={formData.endTime}
                    onChange={(v) => handleInputChange("endTime", v)}
                    error={!!errors.endTime}
                    helperText={errors.endTime}
                    disabled={isViewMode}
                    minTime={
                      formData.startTime
                        ? (() => {
                            const totalMins = toMinutes(formData.startTime) + 1;
                            const h = Math.floor(totalMins / 60);
                            const m = totalMins % 60;
                            return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
                          })()
                        : undefined
                    }
                  />
                </Box>
              </Box>
            </Box>
            <Box flex={1}>
              <FormLabel>Base Ticket Price *</FormLabel>
              <TextField
                fullWidth
                type="number"
                sx={textFieldSX}
                value={standardPriceInput}
                placeholder="₹150"
                onChange={(e) => {
                  const rawValue = e.target.value;
                  standardPriceRef.current = rawValue;
                  setStandardPriceInput(rawValue);
                  handleInputChange(
                    "standardPrice",
                    rawValue === "" ? 0 : sanitizeNonNegativeNumber(Number(rawValue)),
                  );
                }}
                error={!!errors.standardPrice}
                helperText={errors.standardPrice}
                disabled={isViewMode}
                inputProps={{
                  min: 0,
                  step: 1,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
        <Box
          sx={{
            flex: 1,
            backgroundColor: "#fff",
            borderRadius: "16px",
            border: "1px solid #E7EEDC",
            p: 2,
          }}
        >
          <FormLabel>Event Gallery *</FormLabel>
          <Box
            onClick={() => {
              if (isViewMode) return;
              galleryInputRef.current?.click();
            }}
            onDragOver={handleGalleryDragOver}
            onDragLeave={handleGalleryDragLeave}
            onDrop={handleGalleryDrop}
            sx={{
              border: `1px dashed ${errors.gallery ? "red" : "#D9E2CF"}`,
              borderRadius: "12px",
              p: isMobile ? 2 : 4,
              textAlign: "center",
              cursor: isViewMode ? "default" : "pointer",
              bgcolor: isDragActiveGallery ? "#EEF7E9" : "#FAFAFA",
            }}
          >
            <input
              ref={galleryInputRef}
              type="file"
              hidden
              multiple
              accept="image/jpeg,image/png,image/jpg"
              onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                const files = Array.from(e.target.files || []);
                if (files.length) {
                  const currentGallery = Array.isArray(formData.gallery)
                    ? formData.gallery
                    : [];
                  
                  const validFiles: File[] = [];
                  let lastError = "";

                  for (const file of files) {
                    const error = validateFile(file);
                    if (error) {
                      lastError = error;
                    } else {
                      try {
                        const resizedFile = await resizeFile(file);
                        validFiles.push(resizedFile);
                      } catch (err) {
                        console.error(err);
                        validFiles.push(file);
                      }
                    }
                  }

                  if (lastError) {
                    setErrors((prev) => ({ ...prev, gallery: lastError }));
                    return;
                  }

                  if (
                    existingGallery.length +
                      currentGallery.length +
                      validFiles.length >
                    3
                  ) {
                    setSnackbarMessage(
                      "You can only upload up to 3 images in the Event Gallery.",
                    );
                    setSnackbarType("error");
                    setSnackbarOpen(true);
                    return;
                  }
                  handleInputChange("gallery", [...currentGallery, ...validFiles]);
                  setSelectedThumbIndex(-1);
                }
                if (galleryInputRef.current) {
                  galleryInputRef.current.value = "";
                }
              }}
              disabled={isViewMode}
            />
            <CloudUploadOutlinedIcon
              sx={{
                fontSize: 40,
                color: "#7BC043",
              }}
            />
            {!isViewMode && (
              <>
                <Typography mt={1} fontSize="1rem">
                  Click to{" "}
                  <b style={{ color: "#7BC043" }}>Upload Gallery Default</b>
                </Typography>
                <Typography fontSize="12px" color="text.secondary">
                  PNG or JPG Max 5MB | Max 3 images
                </Typography>
              </>
            )}
          </Box>
          {errors.gallery && (
            <Typography
              variant="caption"
              color="error"
              sx={{
                mt: 0,
                display: "block",
                marginLeft: "0px",
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                fontWeight: 400,
                fontSize: "0.75rem",
                lineHeight: 1.66,
                letterSpacing: "0.03333em",
              }}
            >
              {errors.gallery}
            </Typography>
          )}
          <Box mt={2} display="flex" flexWrap="wrap" gap={1.5}>
            {/* Existing saved gallery images */}
            {existingGallery.map((src, idx) => (
              <Box
                key={`ex-${idx}`}
                sx={{
                  position: "relative",
                  width: 80,
                  height: 80,
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #E7EEDC",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={
                    src.startsWith("data:") || src.startsWith("http")
                      ? src
                      : `data:image/*;base64,${src}`
                  }
                  alt={`Gallery ${idx + 1}`}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                {!isViewMode && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      const updated = existingGallery.filter((_, i) => i !== idx);
                      setExistingGallery(updated);
                      // If thumbnail was this image, clear it
                      if (existingThumbnail === src) {
                        setExistingThumbnail(null);
                      }
                    }}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      bgcolor: "rgba(255,255,255,0.9)",
                      padding: "2px",
                      width: 20,
                      height: 20,
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "0.75rem" }} />
                  </IconButton>
                )}
              </Box>
            ))}
            {/* Newly uploaded gallery files */}
            {(formData.gallery || []).map((file, idx) => (
              <Box
                key={file.name + idx}
                sx={{
                  position: "relative",
                  width: 80,
                  height: 80,
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid #E7EEDC",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                {!isViewMode && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newGallery = [...(formData.gallery || [])];
                      newGallery.splice(idx, 1);
                      handleInputChange("gallery", newGallery);
                      if (selectedThumbIndex === idx) setSelectedThumbIndex(-1);
                    }}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      bgcolor: "rgba(255,255,255,0.9)",
                      padding: "2px",
                      width: 20,
                      height: 20,
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "0.75rem" }} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            backgroundColor: "#fff",
            borderRadius: "16px",
            border: "1px solid #E7EEDC",
            p: 2,
          }}
        >
          <FormLabel>Select Thumbnail *</FormLabel>
          <Box
            onClick={() => {
              if (isViewMode) return;
              thumbnailInputRef.current?.click();
            }}
            onDragOver={handleThumbnailDragOver}
            onDragLeave={handleThumbnailDragLeave}
            onDrop={handleThumbnailDrop}
            sx={{
              border: `1px dashed ${errors.thumbnail ? "red" : "#D9E2CF"}`,
              borderRadius: "12px",
              p: isMobile ? 2 : 4,
              textAlign: "center",
              cursor: isViewMode ? "default" : "pointer",
              bgcolor: isDragActiveThumbnail ? "#EEF7E9" : "#FAFAFA",
            }}
          >
            <input
              ref={thumbnailInputRef}
              type="file"
              hidden
              accept="image/jpeg,image/png,image/jpg"
              disabled={isViewMode}
              onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0] || null;
                if (file) {
                  const error = validateFile(file);
                  if (error) {
                    setErrors((prev) => ({ ...prev, thumbnail: error }));
                    return;
                  }
                  try {
                    const resizedFile = await resizeFile(file);
                    handleInputChange("thumbnail", resizedFile);
                  } catch (err) {
                    console.error(err);
                    handleInputChange("thumbnail", file);
                  }
                  setSelectedThumbIndex(-1);
                }
              }}
            />
            {currentThumbnailPreview ? (
              <Box position="relative" display="inline-block">
                <Box
                  component="img"
                  src={
                    currentThumbnailPreview?.startsWith("data:") ||
                    currentThumbnailPreview?.startsWith("blob:")
                      ? currentThumbnailPreview
                      : `data:image/*;base64,${currentThumbnailPreview}`
                  }
                  alt="thumbnail"
                  sx={{
                    maxHeight: "180px",
                    maxWidth: "100%",
                    borderRadius: "8px",
                  }}
                />
                {!isViewMode && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInputChange("thumbnail", null);
                      setSelectedThumbIndex(-1);
                      if (thumbnailInputRef.current) {
                        thumbnailInputRef.current.value = "";
                      }
                    }}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "white",
                      padding: "4px",
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "1.2rem" }} />
                  </IconButton>
                )}
              </Box>
            ) : (
              <>
                <CloudUploadOutlinedIcon
                  sx={{
                    fontSize: 40,
                    color: "#7BC043",
                  }}
                />
                {!isViewMode && (
                  <>
                    <Typography mt={1} fontSize="1rem">
                      Click to{" "}
                      <b style={{ color: "#7BC043" }}>Upload Thumbnail</b>
                    </Typography>
                    <Typography fontSize="12px" color="text.secondary">
                      PNG or JPG Max 5MB
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>
          {errors.thumbnail && (
            <Typography
              variant="caption"
              color="error"
              sx={{
                mt: 0,
                display: "block",
                marginLeft: "0px",
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                fontWeight: 400,
                fontSize: "0.75rem",
                lineHeight: 1.66,
                letterSpacing: "0.03333em",
              }}
            >
              {errors.thumbnail}
            </Typography>
          )}

          <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
            {(formData.gallery || []).map((file, idx) => {
              const previewUrl = URL.createObjectURL(file);
              return (
                <Box
                  key={`${file.name}-${idx}`}
                  onClick={() => {
                    if (isViewMode) return;
                    setSelectedThumbIndex(idx);
                    handleInputChange("thumbnail", null);
                  }}
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "8px",
                    border:
                      selectedThumbIndex === idx
                        ? "2px solid #7BC043"
                        : "1px solid #D9E2CF",
                    overflow: "hidden",
                    cursor: isViewMode ? "default" : "pointer",
                  }}
                >
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="thumb"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>
              );
            })}

            {!formData.gallery?.length && existingThumbnail && (
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "8px",
                  border: "1px solid #D9E2CF",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={`data:image/*;base64,${existingThumbnail}`}
                  alt="existing-thumb"
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid #E7EEDC",
          p: 2,
        }}
      >
        <Box display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
          <Box flex={1}>
            <FormLabel>Instructors *</FormLabel>
            <Autocomplete
              multiple
              options={instructorOptions}
              value={instructorOptions.filter((option: any) =>
                formData.instructors.includes(option.value),
              )}
              disabled={isViewMode}
              onChange={(_, values) => {
                handleInputChange(
                  "instructors",
                  values.map((value: any) => value.value),
                );
              }}
              getOptionLabel={(option: any) => option.label}
              renderTags={(value, getTagProps) =>
                value.map((option: any, index: number) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.label}
                    sx={{ backgroundColor: "#E8F7EC", color: "#2E7D32" }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={textFieldSX}
                  placeholder="Select instructor..."
                  error={!!errors.instructors}
                  helperText={errors.instructors}
                />
              )}
            />
          </Box>
          <Box flex={1}>
            <FormLabel>Description *</FormLabel>
            <TextField
              fullWidth
              multiline
              rows={4}
              sx={textFieldSX}
              value={formData.description}
              placeholder="A dance show is an opportunity to share the beauty and power of your chosen style of dance..."
              onChange={(e) => handleInputChange("description", e.target.value)}
              disabled={isViewMode}
              error={!!errors.description}
              helperText={errors.description}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid #E7EEDC",
          p: 2,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography
            fontWeight={600}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            Special Offers
          </Typography>
          {!isViewMode && (
            <Button
              variant="outlined"
              size="small"
              sx={{
                borderColor: themestyle.colors.buttonGreen,
                color: themestyle.colors.activeGreen,
              }}
              onClick={() =>
                handleInputChange("offers", [
                  ...formData.offers,
                  {
                    promoCode: "",
                    offerStartDate: "",
                    offerEndDate: "",
                    offerPrice: Number(formData.standardPrice) || 0,
                    discountAmount: "",
                    discountPercent: "",
                  },
                ])
              }
            >
              + Add Offer
            </Button>
          )}
        </Box>

        {formData.offers.map((offer, idx) => (
          <Box
            key={`offer-${idx}`}
            display="flex"
            gap={2}
            flexDirection={{ xs: "column", md: "row" }}
            mb={2}
            alignItems="center"
          >
            <Box flex={1} minWidth={{ xs: "100%", md: "150px" }}>
              <FormLabel>Offer Name *</FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                value={offer.promoCode}
                placeholder="e.g., Early Bird"
                disabled={isViewMode}
                onChange={(e) => {
                  const newOffers = [...formData.offers];
                  newOffers[idx].promoCode = e.target.value;
                  handleInputChange("offers", newOffers);
                }}
                error={!!errors[`offerPromoCode_${idx}`]}
                helperText={errors[`offerPromoCode_${idx}`]}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>Discount (%) *</FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                type="number"
                value={
                  offer.discountPercent !== undefined
                    ? offer.discountPercent
                    : offer.offerPrice !== undefined &&
                      offer.offerPrice !== null &&
                      !isNaN(Number(formData.standardPrice)) &&
                      Number(formData.standardPrice) > 0
                    ? parseFloat(
                        (((Number(formData.standardPrice) -
                          Number(offer.offerPrice)) /
                          Number(formData.standardPrice)) *
                          100).toFixed(2)
                      ).toString()
                    : ""
                }
                placeholder="Enter Discount"
                onChange={(e) => {
                  const newOffers = [...formData.offers];
                  const val = e.target.value;
                  const discountVal = Math.round(Number(val));
                  newOffers[idx].discountPercent = discountVal.toString();
                  const standardPrice = Number(formData.standardPrice) || 0;

                  const discountError = validateOfferDiscountPercent(discountVal.toString());
                  
                  if (!discountError) {
                    const price = standardPrice - (standardPrice * discountVal) / 100;
                    newOffers[idx].offerPrice = Math.max(0, parseFloat(price.toFixed(2)));
                    newOffers[idx].discountAmount = parseFloat((standardPrice - newOffers[idx].offerPrice).toFixed(2)).toString();
                  }
                  handleInputChange("offers", newOffers);
                  setErrors((prev) => ({
                    ...prev,
                    [`offerDiscountPercent_${idx}`]: discountError,
                  }));
                }}
                disabled={isViewMode}
                error={!!errors[`offerDiscountPercent_${idx}`]}
                helperText={errors[`offerDiscountPercent_${idx}`]}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 1,
                }}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>Offer Start Date *</FormLabel>
              <AppDatePicker
                fullWidth
                value={offer.offerStartDate}
                onChange={(v) => {
                  const newOffers = [...formData.offers];
                  newOffers[idx].offerStartDate = v;
                  handleInputChange("offers", newOffers);
                }}
                disabled={isViewMode}
                error={!!errors[`offerStartDate_${idx}`]}
                helperText={errors[`offerStartDate_${idx}`]}
                minDate={formData.startDate || currentGlobalDate}
                maxDate={formData.endDate || undefined}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>Offer End Date *</FormLabel>
              <AppDatePicker
                fullWidth
                value={offer.offerEndDate}
                onChange={(v) => {
                  const newOffers = [...formData.offers];
                  newOffers[idx].offerEndDate = v;
                  handleInputChange("offers", newOffers);
                }}
                disabled={isViewMode}
                error={!!errors[`offerEndDate_${idx}`]}
                helperText={errors[`offerEndDate_${idx}`]}
                minDate={offer.offerStartDate || formData.startDate || currentGlobalDate}
                maxDate={formData.endDate || undefined}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>Discount Amount *</FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                value={
                  offer.discountAmount !== undefined
                    ? offer.discountAmount
                    : offer.offerPrice !== undefined && offer.offerPrice !== null
                    ? parseFloat(
                        (Number(formData.standardPrice) -
                          Number(offer.offerPrice)).toFixed(2)
                      ).toString()
                    : ""
                }
                placeholder="Enter Amount"
                onChange={(e) => {
                  const newOffers = [...formData.offers];
                  const val = e.target.value;
                  newOffers[idx].discountAmount = val;

                  const discountAmount = Number(val);
                  const standardPrice = Number(formData.standardPrice) || 0;

                  if (!isNaN(discountAmount)) {
                    const finalPrice = standardPrice - discountAmount;
                    newOffers[idx].offerPrice = Math.max(
                      0,
                      parseFloat(finalPrice.toFixed(2)),
                    );
                    if (standardPrice > 0) {
                      newOffers[idx].discountPercent = parseFloat(((discountAmount / standardPrice) * 100).toFixed(2)).toString();
                    }
                  }
                  handleInputChange("offers", newOffers);
                }}
                disabled={isViewMode}
                error={!!errors[`offerPrice_${idx}`]}
                helperText={errors[`offerPrice_${idx}`]}
              />
            </Box>

            {!isViewMode && (
              <Button
                color="error"
                onClick={() => {
                  const newOffers = formData.offers.filter((_, i) => i !== idx);
                  handleInputChange("offers", newOffers);
                }}
                sx={{ mt: 3.5 }}
              >
                Delete
              </Button>
            )}
          </Box>
        ))}
        {formData.offers.length === 0 && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            No special offers added yet.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          px: isMobile ? 1 : { sm: 3, md: 5 },
          pb: 3,
          display: "flex",
          justifyContent: "flex-end",
          gap: isMobile ? 1 : 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            borderColor: themestyle.colors.buttonGreen,
            color: themestyle.colors.black,
            textTransform: "none",
            fontWeight: 600,
            px: isMobile ? 1.5 : 2,
            borderRadius: "8px",
            height: isMobile ? "36px" : "44px",
            fontSize: isMobile ? "13px" : "16px",
            minWidth: isMobile ? "75px" : "110px",
          }}
        >
          Cancel
        </Button>

        {mode === "view" && id ? (
          <Button
            variant="contained"
            onClick={onEdit}
            sx={{
              backgroundColor: themestyle.colors.buttonGreen,
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              px: isMobile ? 1.5 : 5,
              height: isMobile ? "36px" : "44px",
              fontSize: isMobile ? "13px" : "16px",
              minWidth: isMobile ? "95px" : "110px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: themestyle.colors.buttonGreenHover,
              },
            }}
          >
            Edit
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: themestyle.colors.buttonGreen,
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              px: isMobile ? 1.5 : 5,
              height: isMobile ? "36px" : "44px",
              fontSize: isMobile ? "13px" : "16px",
              minWidth: isMobile ? "85px" : "110px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: themestyle.colors.buttonGreenHover,
              },
              "&.Mui-disabled": { backgroundColor: "#A5D68B", color: "#fff" },
            }}
          >
            {mode === "edit" ? "Update" : "Save"}
          </Button>
        )}
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default AddEventForm;
